import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { MilestoneEvent } from '../../_models/milestone/milestone-event.model';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';

/**
 * Learner milestone events (api/Milestone) — dated timeline notes for out-of-session events that
 * impact a learner's progress. See SDD/specs/LearnerMilestoneEvents.md (TutorPortalAPI repo).
 * No submit/approve lifecycle: an event is live on the timeline as soon as it is saved.
 */
@Injectable({ providedIn: 'root' })
export class MilestoneService extends BaseService {
  private apiUrl: string = environment.apiUrl;

  constructor(
    private http: HttpClient,
    protected hashManagerService: SessionUserHashManagerService
  ) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  /** A learner's milestone events (newest first), each carrying student name + creator name. */
  list(placementId: number, studentId: number): Observable<MilestoneEvent[]> {
    const payload = { placementId, studentId };
    return this.http.post(this.apiUrl + 'api/Milestone/list', payload, this.prepareOptions(payload)).pipe(
      map(this.unwrap), catchError(this.handleError('list'))
    );
  }

  /** Create (id 0) or edit a milestone event. Returns the saved event id. */
  save(milestone: MilestoneEvent): Observable<number> {
    return this.http.post(this.apiUrl + 'api/Milestone/save', milestone, this.prepareOptions(milestone)).pipe(
      map(this.unwrap), catchError(this.handleError('save'))
    );
  }

  /** Delete a saved milestone event. Returns true when a row was removed. */
  deleteById(id: number): Observable<boolean> {
    const payload = { id };
    return this.http.post(this.apiUrl + 'api/Milestone/delete', payload, this.prepareOptions(payload)).pipe(
      map(this.unwrap), catchError(this.handleError('deleteById'))
    );
  }

  /** Body unwrap — business-validation failures arrive on the error channel (handleError). */
  private unwrap = (data: any) => data.body;
}
